/** Wires player component events to the event crawl via scene-level event emission. */
import { Player } from '../entities/Player';
import { ConfigLoader } from '../systems/ConfigLoader';
import { DamageNumbers } from '../systems/DamageNumbers';

/**
 * Wire player component events (XP gains, node unlocks, fork choices, skill activations)
 * to the event crawl overlay and damage number display.
 */
export function wireCrawlOverlay(
  player: Player,
  sceneEvents: Phaser.Events.EventEmitter,
  damageNumbers: DamageNumbers,
): void {
  const pushCrawl = (text: string, color: string) => sceneEvents.emit('crawl-push', { text, color });

  // XP gains
  player.events.on('tree-xp-gained', (data: { treeName: string; skillId: string; xpGained: number; totalXp: number }) => {
    const xp = Math.floor(data.xpGained * 100) / 100;
    if (xp > 0) {
      pushCrawl(`+${xp} ${data.treeName} XP (${Math.floor(data.totalXp)})`, '#44dd44');
    }
  });

  // Node unlocks
  player.events.on('node-unlocked', (data: { treeName: string; nodeId: string; nodeType: string }) => {
    const node = ConfigLoader.getV4Node(data.nodeId);
    const name = node?.name ?? data.nodeId;
    const typeLabel = data.nodeType === 'passive' ? 'passive' :
      data.nodeType === 'upgrade' ? 'upgrade' :
      data.nodeType === 'evolution' ? 'evolution' : 'skill';
    pushCrawl(`Unlocked ${typeLabel}: ${name}`, '#ffdd44');

    // Evolution nodes auto-replace their parent skill in the hotbar
    if (data.nodeType === 'evolution' && node && 'replacesId' in node && node.replacesId) {
      player.skillActivation.unregisterEffect(node.replacesId);
      player.attunement.replaceSkill(node.replacesId, data.nodeId);
    }
  });

  // Fork choices
  player.events.on('fork-chosen', (data: { treeName: string; forkId: string; chosenNodeId: string }) => {
    const node = ConfigLoader.getV4Node(data.chosenNodeId);
    pushCrawl(`Path chosen: ${node?.name ?? data.chosenNodeId}`, '#ff8844');
  });

  // Skill activations
  player.events.on('skill-activated', (data: { skillId: string }) => {
    const name = ConfigLoader.getV4Node(data.skillId)?.name ?? data.skillId;
    pushCrawl(`${name} activated`, '#44ddff');
  });
  player.events.on('skill-deactivated', (data: { skillId: string }) => {
    // Only show "ended" for sustained skills — momentary skills (punch, stab, etc.) are too brief to matter.
    const effect = player.skillActivation.getEffect(data.skillId);
    // Suppress hide's "ended" when stealth is also active — stealth's message covers both.
    if (data.skillId === 'hide') {
      const stealth = player.skillActivation.getEffect('stealth');
      if (stealth?.isActive?.()) return;
    }
    // Concealment skills declare 'instant' (not manually toggled off) but are sustained — include them.
    const isConceal = data.skillId === 'hide' || data.skillId === 'stealth';
    if (effect?.activationMode !== 'toggle' && !isConceal) return;
    const name = ConfigLoader.getV4Node(data.skillId)?.name ?? data.skillId;
    pushCrawl(`${name} ended`, '#888888');
  });
  // Skill blocked (canActivate gate rejection) — subtle feedback, throttled per-skill
  const blockedThrottles = new Map<string, number>();
  const BLOCKED_THROTTLE_MS = 500;
  player.events.on('skill-blocked', (data: { skillId: string; reason: string }) => {
    const now = Date.now();
    const last = blockedThrottles.get(data.skillId) ?? 0;
    if (now - last < BLOCKED_THROTTLE_MS) return;
    blockedThrottles.set(data.skillId, now);

    const cfg = ConfigLoader.combat.damageNumbers;
    const label = data.reason.toUpperCase();
    damageNumbers.spawnLabel(
      player.sprite.x, player.sprite.y,
      label, '#999999', `${cfg.minFontSizePx}px`,
    );
    const name = ConfigLoader.getV4Node(data.skillId)?.name ?? data.skillId;
    pushCrawl(`${name}: ${label.toLowerCase()}`, '#888888');
  });

  player.events.on('skill-failed', (data: { skillId: string }) => {
    const cfg = ConfigLoader.combat.damageNumbers;
    const effect = player.skillActivation.getEffect(data.skillId);
    const label = effect?.failLabel ?? 'FAIL';
    damageNumbers.spawnLabel(
      player.sprite.x, player.sprite.y,
      label, cfg.dealtColor, `${cfg.baseFontSizePx}px`,
    );
    const name = ConfigLoader.getV4Node(data.skillId)?.name ?? data.skillId;
    pushCrawl(`${name} ${label}`, '#ff4444');
  });
}
